// Lifecycle presentation config for the listing-request list + detail views.
//
// The seller-service returns the canonical lifecycle projection — `stage`,
// `stageStep`, `stageTotal`, `stageState` — collapsed from its 13-state machine.
// admin-web renders the SAME projection off the same wire fields; only the copy
// below differs. Never derive a stage from `requestStatus` here: a second
// hand-written collapse is exactly how the two portals drifted apart.
//
// i18n is not wired yet, so copy lives as plain strings (per repo convention).
import type { ChipColor } from '@uiKit/elements/Chip.vue'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'

// The five pipeline stages emitted by the backend (ListingRequestStage).
export type ListingRequestStage =
  | 'review'
  | 'owner_verification'
  | 'agreement'
  | 'owner_onboarding'
  | 'listed'

// How the current stage is going (ListingRequestStageState). Orthogonal to the
// stage: "sent back for revision" is the state of stage 1, not a sixth stage.
export type ListingRequestStageState =
  | 'in_progress'
  | 'attention'
  | 'completed'
  | 'rejected'
  | 'withdrawn'

// Typed stage values — the single source of truth for comparing `stage`.
// Consumers (detail container, dashboard KPIs) key off these instead of loose
// string literals so a backend rename breaks the type-check, not silently the
// counts. `satisfies` guarantees every value stays a valid ListingRequestStage.
export const LISTING_STAGE = {
  review: 'review',
  ownerVerification: 'owner_verification',
  agreement: 'agreement',
  ownerOnboarding: 'owner_onboarding',
  listed: 'listed',
} as const satisfies Record<string, ListingRequestStage>

export const LISTING_STAGE_STATE = {
  inProgress: 'in_progress',
  attention: 'attention',
  completed: 'completed',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
} as const satisfies Record<string, ListingRequestStageState>

export interface StageMeta {
  label: string
  color: ChipColor
}

// Status chip copy per stage, for a request that is progressing normally.
const STAGE_META: Record<ListingRequestStage, StageMeta> = {
  review: { label: 'Under review', color: 'dark' },
  owner_verification: { label: 'Ownership check', color: 'primary' },
  agreement: { label: 'Agreement', color: 'primary' },
  owner_onboarding: { label: 'Account setup', color: 'primary' },
  listed: { label: 'Listed', color: 'success' },
}

// A non-normal state overrides the stage label — the seller cares far more that
// something stopped than which stage it stopped on. `attention` is the exception:
// what is needed depends on the stage, so it resolves per stage below.
const STATE_META: Partial<Record<ListingRequestStageState, StageMeta>> = {
  completed: { label: 'Listed', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
  withdrawn: { label: 'Withdrawn', color: 'neutral' },
}

const ATTENTION_META: Partial<Record<ListingRequestStage, StageMeta>> = {
  review: { label: 'Action required', color: 'warning' },
  agreement: { label: 'Agreement declined', color: 'warning' },
}

const ATTENTION_META_FALLBACK: StageMeta = { label: 'Action required', color: 'warning' }
export const STAGE_META_FALLBACK: StageMeta = { label: 'In progress', color: 'neutral' }

export function stageMetaOf(stage: string, state: string): StageMeta {
  if (state === LISTING_STAGE_STATE.attention) {
    return ATTENTION_META[stage as ListingRequestStage] ?? ATTENTION_META_FALLBACK
  }
  return (
    STATE_META[state as ListingRequestStageState] ??
    STAGE_META[stage as ListingRequestStage] ??
    STAGE_META_FALLBACK
  )
}

// The 5 canonical lifecycle steps rendered by the LifecycleStepper. `stageStep`
// (1-5) from the backend drives done/active/upcoming.
//
// Step 4 is admin-side work the seller cannot act on, and it is shown anyway:
// folding it into "Completed" is what used to tell the seller their listing was
// ready to go live while ops still had owner onboarding and promotion to do.
export interface LifecycleStepMeta {
  step: number
  label: string
  caption: string
}

export const LIFECYCLE_STEPS: LifecycleStepMeta[] = [
  { step: 1, label: 'Under review', caption: 'Docs & title deed' },
  { step: 2, label: 'Ownership check', caption: 'Verifying owners' },
  { step: 3, label: 'Agreement', caption: 'MOU & signing' },
  { step: 4, label: 'Account setup', caption: 'Owner accounts' },
  { step: 5, label: 'Listed', caption: 'Live for investors' },
]

// Tone of the stepper's active step + the hero's accent stripe, derived from the
// stage STATE (never the stage itself).
export type StageTone = 'normal' | 'attention' | 'negative'

export function stageToneOf(state: string): StageTone {
  if (state === LISTING_STAGE_STATE.rejected || state === LISTING_STAGE_STATE.withdrawn) {
    return 'negative'
  }
  if (state === LISTING_STAGE_STATE.attention) return 'attention'
  return 'normal'
}

// True once the pipeline has finished — the stepper marks every node done rather
// than leaving the last one mid-flight.
export function isStagePipelineComplete(state: string): boolean {
  return state === LISTING_STAGE_STATE.completed
}

// Tone shared by the detail-screen surfaces (status rail, stepper, timeline).
// Maps 1:1 onto the `--p-<tone>-*` token ramps, with `neutral` = surface/muted.
export type LifecycleTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

// Tinted-surface token pair per tone. Returned as `var(--p-*)` strings (not CSS
// classes) for the few kit components that take colors as props and apply them
// inline — `IconBox`'s `bg` / `iconColor`. Scoped CSS is still the default
// everywhere else; this exists only for that prop-driven seam.
export interface ToneSurface {
  bg: string
  fg: string
}

const TONE_SURFACES: Record<LifecycleTone, ToneSurface> = {
  neutral: { bg: 'var(--p-surface-100)', fg: 'var(--p-text-muted-color)' },
  primary: { bg: 'var(--p-primary-50)', fg: 'var(--p-primary-600)' },
  success: { bg: 'var(--p-success-50)', fg: 'var(--p-success-600)' },
  warning: { bg: 'var(--p-warning-50)', fg: 'var(--p-warning-600)' },
  danger: { bg: 'var(--p-danger-50)', fg: 'var(--p-danger-600)' },
  info: { bg: 'var(--p-info-50)', fg: 'var(--p-info-600)' },
}

export function toneSurface(tone: LifecycleTone): ToneSurface {
  return TONE_SURFACES[tone]
}

// "What happens next" copy for the detail screen's status rail. The seller's
// most-asked question on this page is "what am I waiting for?" — this answers
// it per stage instead of leaving them to infer it from the stepper.
export interface StageOutlook {
  headline: string
  detail: string
  icon: PiIconName
  tone: LifecycleTone
}

// Normal progress, one entry per stage.
const STAGE_OUTLOOK: Record<ListingRequestStage, StageOutlook> = {
  review: {
    headline: 'Under review',
    detail:
      'Our team is verifying your title deed and property details. You’ll be notified as soon as there’s an update.',
    icon: 'hourglass',
    tone: 'primary',
  },
  owner_verification: {
    headline: 'Checking ownership',
    detail:
      'Your property passed review. We’re confirming every declared owner against the title deed — nothing needed from you.',
    icon: 'verified',
    tone: 'primary',
  },
  agreement: {
    headline: 'Signatures pending',
    detail: 'Sign the agreements below. Every owner must sign before the listing can go live.',
    icon: 'file',
    tone: 'primary',
  },
  owner_onboarding: {
    headline: 'Setting up owner accounts',
    detail:
      'Everything is signed. We’re creating an account for each owner — the last step before your property goes live.',
    icon: 'users',
    tone: 'primary',
  },
  listed: {
    headline: 'Listed',
    detail: 'Your property is live for investors. Track it from the Properties tab.',
    icon: 'check-circle',
    tone: 'success',
  },
}

// A non-normal state supersedes the stage copy. `attention` resolves per stage —
// what the seller has to do differs by where it stalled.
const STATE_OUTLOOK: Partial<Record<ListingRequestStageState, StageOutlook>> = {
  rejected: {
    headline: 'Request rejected',
    detail: 'See the reason below. You can start a fresh request once it’s resolved.',
    icon: 'times-circle',
    tone: 'danger',
  },
  withdrawn: {
    headline: 'Request withdrawn',
    detail: 'You withdrew this request. Start a new one whenever you’re ready.',
    icon: 'ban',
    tone: 'neutral',
  },
}

const ATTENTION_OUTLOOK: Partial<Record<ListingRequestStage, StageOutlook>> = {
  review: {
    headline: 'Your input needed',
    detail: 'Update the details we flagged and resubmit — the review continues straight after.',
    icon: 'exclamation-triangle',
    tone: 'warning',
  },
  // An owner declined the agreement. There is nothing to sign until the team
  // re-issues it — telling the seller to "sign the agreements below" here sends
  // them to a panel with no available action.
  agreement: {
    headline: 'Agreement declined',
    detail:
      'One of the owners declined the agreement. Our team is preparing a new one — we’ll let you know when it’s ready to sign.',
    icon: 'exclamation-triangle',
    tone: 'warning',
  },
}

export const STAGE_OUTLOOK_FALLBACK: StageOutlook = {
  headline: 'In progress',
  detail: 'We’re working through this request. Check the activity log below for the latest step.',
  icon: 'clock',
  tone: 'primary',
}

export function stageOutlookOf(stage: string, state: string): StageOutlook {
  if (state === LISTING_STAGE_STATE.attention) {
    return ATTENTION_OUTLOOK[stage as ListingRequestStage] ?? STAGE_OUTLOOK_FALLBACK
  }
  return (
    STATE_OUTLOOK[state as ListingRequestStageState] ??
    STAGE_OUTLOOK[stage as ListingRequestStage] ??
    STAGE_OUTLOOK_FALLBACK
  )
}

// Short list-row guidance for off-path states. The list DTO doesn't carry the
// full revision/rejection text (that's detail-only), so this is UI copy that
// points the seller to the detail page — never fabricated request data.
const ATTENTION_GUIDANCE: Partial<Record<ListingRequestStage, string>> = {
  review: 'Changes requested — open to see what needs fixing.',
  agreement: 'An owner declined the agreement — open for details.',
}

const STATE_GUIDANCE: Partial<Record<ListingRequestStageState, string>> = {
  rejected: 'This request was rejected — open for the reason.',
  withdrawn: 'You withdrew this request.',
}

export function stageGuidanceOf(stage: string, state: string): string | null {
  if (state === LISTING_STAGE_STATE.attention) {
    return ATTENTION_GUIDANCE[stage as ListingRequestStage] ?? null
  }
  return STATE_GUIDANCE[state as ListingRequestStageState] ?? null
}

// Status filter options for the list toolbar. Empty value = "All".
export interface StatusFilterOption {
  label: string
  value: string
}

// Values are raw `requestStatus` (what the list endpoint filters on); labels use
// the same stage vocabulary the chips and stepper speak, so the toolbar and the
// rows can't describe the same request with two different words.
export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { label: 'All requests', value: '' },
  { label: 'Under review', value: 'submitted' },
  { label: 'Action required', value: 'revision_requested' },
  { label: 'Ownership check', value: 'owner_verification' },
  { label: 'Agreement', value: 'agreement_in_progress' },
  { label: 'Agreement declined', value: 'agreement_rejected' },
  { label: 'Account setup', value: 'owners_onboarding' },
  { label: 'Listed', value: 'promoted' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Withdrawn', value: 'withdrawn' },
]

// Human labels for timeline event types (RequestEventType). Fallback prettifies
// any unknown snake_case type.
export const EVENT_TYPE_LABELS: Record<string, string> = {
  status_changed: 'Status updated',
  price_proposed_by_seller: 'You proposed a price',
  price_countered_by_admin: 'Admin proposed a price',
  price_accepted: 'Price accepted',
  price_rejected: 'Price rejected',
  document_uploaded: 'Document uploaded',
  note_added: 'Note added',
  agreement_generated: 'Agreement generated',
  agreement_sent: 'Agreement sent for signing',
  agreement_signed: 'Agreement signed',
  agreement_declined: 'Agreement declined',
  agreement_completed: 'Agreement completed',
}

export function eventLabelOf(eventType: string): string {
  return (
    EVENT_TYPE_LABELS[eventType] ??
    eventType.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase())
  )
}

// Icon + tone per timeline event type, so the activity log is scannable at a
// glance (green = progressed, amber = waiting on someone, red = blocked).
export interface EventVisual {
  icon: PiIconName
  tone: LifecycleTone
}

export const EVENT_VISUALS: Record<string, EventVisual> = {
  status_changed: { icon: 'refresh', tone: 'info' },
  price_proposed_by_seller: { icon: 'tag', tone: 'primary' },
  price_countered_by_admin: { icon: 'tag', tone: 'warning' },
  price_accepted: { icon: 'check-circle', tone: 'success' },
  price_rejected: { icon: 'times-circle', tone: 'danger' },
  document_uploaded: { icon: 'upload', tone: 'info' },
  note_added: { icon: 'comments', tone: 'neutral' },
  agreement_generated: { icon: 'file', tone: 'neutral' },
  agreement_sent: { icon: 'send', tone: 'warning' },
  agreement_signed: { icon: 'verified', tone: 'success' },
  agreement_declined: { icon: 'ban', tone: 'danger' },
  agreement_completed: { icon: 'check-circle', tone: 'success' },
}

export const EVENT_VISUAL_FALLBACK: EventVisual = { icon: 'circle', tone: 'neutral' }

export function eventVisualOf(eventType: string): EventVisual {
  return EVENT_VISUALS[eventType] ?? EVENT_VISUAL_FALLBACK
}

// Agreement status → chip color (AgreementStatus). Fallback = neutral.
export const AGREEMENT_STATUS_META: Record<string, StageMeta> = {
  draft: { label: 'Draft', color: 'neutral' },
  generated: { label: 'Generated', color: 'neutral' },
  sent: { label: 'Awaiting signature', color: 'warning' },
  partially_signed: { label: 'Partially signed', color: 'warning' },
  signed: { label: 'Signed', color: 'primary' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
  expired: { label: 'Expired', color: 'danger' },
  voided: { label: 'Voided', color: 'neutral' },
}

export function agreementStatusMetaOf(status: string): StageMeta {
  return AGREEMENT_STATUS_META[status] ?? STAGE_META_FALLBACK
}

// Human labels for agreement types (AgreementType).
export const AGREEMENT_TYPE_LABELS: Record<string, string> = {
  mou: 'MOU',
  seller_agreement: "Seller's Agreement",
}

export function agreementTypeLabelOf(type: string): string {
  return AGREEMENT_TYPE_LABELS[type] ?? type.toUpperCase()
}

// Signatory signing status → chip label + color (SignatoryStatus).
export const SIGNATORY_STATUS_META: Record<string, StageMeta> = {
  pending: { label: 'Pending', color: 'neutral' },
  sent: { label: 'Sent', color: 'warning' },
  viewed: { label: 'Viewed', color: 'warning' },
  signed: { label: 'Signed', color: 'success' },
  declined: { label: 'Declined', color: 'danger' },
}

export function signatoryStatusMetaOf(status: string): StageMeta {
  return SIGNATORY_STATUS_META[status] ?? STAGE_META_FALLBACK
}

// Signatory roles (AgreementSignatoryRole). `PLATFORM` is SliceAL's own signature
// on the agreement — the seller sees that row's status but never acts on it, so
// nothing here may offer them its signing link. That signature is minted and
// forwarded from the admin portal.
export const AGREEMENT_SIGNATORY_ROLE = {
  PRIMARY_OWNER: 'primary_owner',
  CO_OWNER: 'co_owner',
  PLATFORM: 'platform',
  WITNESS: 'witness',
} as const

// Page size for the paginated list (useInfiniteQuery).
export const LISTINGS_PAGE_LIMIT = 10

// ── Phase B seller-action guards (mirror the backend preconditions) ─────────

// `requestStatus` values from which the seller may withdraw their own request.
export const WITHDRAWABLE_STATUSES: ReadonlySet<string> = new Set([
  'draft',
  'submitted',
  'under_review',
  'revision_requested',
])

// The only `requestStatus` that permits an edit-&-resubmit.
export const REVISABLE_STATUS = 'revision_requested'

// The `priceNegotiationStatus` that means there's an open admin proposal to
// accept or reject.
export const PRICE_PROPOSAL_OPEN_STATUS = 'proposed'
